import jwt_decode from 'jwt-decode';
import Vue from 'vue';
import Router from 'vue-router';

// Routes
import adminRoutes from './routes/admin';
import editorRoutes from './routes/editor';
import folderRoutes from './routes/folder';
import inspectorRoutes from './routes/inspector';
import publisherRoutes from './routes/publisher';
import reportRoutes from './routes/report';

// eslint-disable-next-line import/no-named-as-default, import/no-named-as-default-member
import store from './store';

import { acquireToken, isInteractiveAuthInProgress } from './services/auth';
import { USERS_GET_CURRENT } from './store/users/actionTypes';
import {
  clearLoadedOnlineUserId,
  getLoadedOnlineUserId,
} from './store/actions';
import onlineService from './services/onlineService';
import { normalizeList } from './services/authorization';

// Single components
const NotFound = () => import('./components/layout/404');
const Callback = () => import('./components/layout/Callback');
const Dashboard = () => import('./components/layout/Dashboard');
const FleetData = () => import('./components/layout/FleetData');
const Login = () => import('./components/layout/Login');
const Logout = () => import('./components/layout/Logout');
const Profile = () => import('./components/layout/Profile');
const Version = () => import('./components/layout/Version');
const NewUserWelcomePage = () =>
  import('./components/layout/NewUserWelcomePage');

const LinkRedirect = () => import('./components/layout/LinkRedirect');

const ImageUpload = () => import('./components/ImageUpload/Index');

const APIDocs = () => import('./views/api-docs');

const TOP_LEVEL_TOOLS = ['admin', 'editor', 'publisher', 'inspector', 'report'];
const WORK_INSTRUCTIONS_LOADING_KEY = 'work-instructions-loading';
const INSTRUCTION_PACKAGES_LOADING_KEY = 'instruction-packages-loading';
const STANDARD_TASKS_LOADING_KEY = 'standard-tasks-loading';

const isWorkInstructionsRoute = (route) => {
  const routeName = String(route?.name || '').toLowerCase();
  const routePath = String(route?.path || '').toLowerCase();
  return routeName.includes('work-instructions') || routePath.includes('/wi/');
};

const isInstructionPackagesRoute = (route) => {
  const routeName = String(route?.name || '').toLowerCase();
  const routePath = String(route?.path || '').toLowerCase();
  return (
    routeName.includes('instruction-packages') ||
    routePath.includes('/instruction-packages')
  );
};

const isStandardTasksRoute = (route) => {
  const routeName = String(route?.name || '').toLowerCase();
  const routePath = String(route?.path || '').toLowerCase();
  return (
    routeName.includes('standard-tasks') ||
    routePath.includes('/standard-tasks')
  );
};

const isStandardTasksListRoute = (route) => {
  const routeName = String(route?.name || '').toLowerCase();
  return routeName === 'editor-standard-tasks-list';
};

const extractAllowedTools = (value) =>
  normalizeList(value)
    .map((tool) => String(tool).trim().toLowerCase())
    .filter((tool) => TOP_LEVEL_TOOLS.includes(tool));

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    ...editorRoutes,
    ...publisherRoutes,
    ...inspectorRoutes,
    ...reportRoutes,
    ...adminRoutes,
    ...folderRoutes,

    {
      path: '/view/profile',
      name: 'my-profile',
      component: Profile,
      meta: { authRequired: true },
    },
    {
      path: '/view/fleetdata',
      name: 'fleet-data',
      component: FleetData,
      meta: { authRequired: true },
    },
    {
      path: '/view/404',
      name: '404',
      component: NotFound,
    },
    // { path: '*', component: NotFound },
    {
      path: '/new-user-welcome-page',
      component: NewUserWelcomePage,
      meta: { authRequired: true },
    },
    { path: '/signin', component: Login },
    { path: '/logout', name: 'logout', component: Logout },
    {
      path: '/view/dashboard',
      alias: '/',
      component: Dashboard,
      meta: { authRequired: true },
    },
    {
      path: '/view/version',
      component: Version,
      meta: { authRequired: true },
    },
    { path: '/callback', name: 'callback-route', component: Callback },

    { path: '/view/image/upload', component: ImageUpload },
    {
      path: '/api/v1/link/:id',
      component: LinkRedirect,
      meta: { authRequired: true },
    },

    { path: '/view/api-docs', component: APIDocs },
    {
      path: '*',
      name: 'not-found',
      component: NotFound,
    },
  ],
});

const hasRouteAuthorization = (route, vuexStore) => {
  const requiresPowerAdmin = Boolean(route.meta?.requiresPowerAdmin);
  const requiredRoles = normalizeList(route.meta?.requiredRoles);
  const requiredPermissions = normalizeList(route.meta?.requiredPermissions);
  const requiredAnyPermissions = normalizeList(
    route.meta?.requiredAnyPermissions
  );

  if (requiresPowerAdmin && !vuexStore.getters.isPowerAdmin) {
    return false;
  }

  const hasRoleAccess =
    requiredRoles.length < 1 ||
    vuexStore.getters.hasRole(requiredRoles) ||
    requiredRoles.some((roleName) => vuexStore.getters.canAccessTool(roleName));

  if (!hasRoleAccess) {
    return false;
  }

  if (
    requiredPermissions.length > 0 &&
    !vuexStore.getters.hasAllPermissions(requiredPermissions)
  ) {
    return false;
  }

  if (
    requiredAnyPermissions.length > 0 &&
    !vuexStore.getters.hasAnyPermission(requiredAnyPermissions)
  ) {
    return false;
  }

  return true;
};

const authorizeLoadedUser = (to, from, next, isDebugWelcomePreview) => {
  const { currentUser } = store.state;

  const isOilUser = currentUser._id === process.env.VUE_APP_OIL_USER_EMAIL;
  const hasLinkParam = to.path.includes('link') || from.path.includes('link');
  const hasTokenParam =
    to.query.token &&
    to.query.token !== '' &&
    from.query.token &&
    from.query.token !== '';

  if (isOilUser && !hasLinkParam && !hasTokenParam) {
    return next('/logout');
  }

  if (isOilUser && hasTokenParam) {
    try {
      const decoded = jwt_decode(to.query.token);
      const isOilTokenValid = decoded.exp * 1000 > Date.now();
      if (!isOilTokenValid) {
        return next('/logout');
      }
    } catch (error) {
      console.error('Failed to validate OIL token.', error);
      return next('/logout');
    }
  }

  const isPendingOrRejected =
    currentUser.isPendingApproval || currentUser.isRejected;
  if (isPendingOrRejected && to.path !== '/new-user-welcome-page') {
    return next('/new-user-welcome-page');
  }

  if (
    !isPendingOrRejected &&
    to.path === '/new-user-welcome-page' &&
    !isDebugWelcomePreview
  ) {
    return next('/view/dashboard');
  }

  const pathSegments = to.path.split('/');
  const isAccessingTool = pathSegments.length > 2;

  if (isAccessingTool) {
    const tool = pathSegments[2];
    const hasPermission = store.getters.canAccessTool(tool);

    if (!hasPermission) {
      console.warn('No Permissions');
      return next('/404');
    }
  }

  if (!hasRouteAuthorization(to, store)) {
    console.warn('No role/permission access');
    return next('/404');
  }

  if (from && from.fullPath) {
    localStorage.setItem('previousRoute', from.fullPath);
  }

  return next();
};

router.beforeEach(async (to, from, next) => {
  if (isWorkInstructionsRoute(to)) {
    store.commit('SET_PROGRESS_BAR', {
      key: WORK_INSTRUCTIONS_LOADING_KEY,
      value: 10,
      message: 'Loading work instructions...',
    });
  }

  if (isInstructionPackagesRoute(to)) {
    store.commit('SET_PROGRESS_BAR', {
      key: INSTRUCTION_PACKAGES_LOADING_KEY,
      value: 10,
      message: 'Loading instruction packages...',
    });
  }

  if (isStandardTasksListRoute(to)) {
    store.commit('SET_PROGRESS_BAR', {
      key: STANDARD_TASKS_LOADING_KEY,
      value: 10,
      message: 'Loading standard tasks...',
    });
  } else if (isStandardTasksRoute(from)) {
    store.commit('SET_PROGRESS_BAR', {
      key: STANDARD_TASKS_LOADING_KEY,
      value: null,
    });
  }

  // Allow tests to bypass auth by setting window.__PLAYWRIGHT_TESTING via Playwright's
  try {
    if (typeof window !== 'undefined' && window.__PLAYWRIGHT_TESTING) {
      return next();
    }
  } catch (e) {
    console.warn(
      'Error checking for Playwright testing flag, defaulting to normal auth flow.',
      e
    );
  }

  const isDebugWelcomePreview =
    process.env.NODE_ENV !== 'production' &&
    to.path === '/new-user-welcome-page' &&
    to.query.debugPreview === '1';

  try {
    // Early return if offline
    if (!(await onlineService.getStatus())) {
      clearLoadedOnlineUserId();
      const user = await store.dispatch(USERS_GET_CURRENT);
      const roles = Array.isArray(user.roles) ? user.roles : [];
      const allowedTools = extractAllowedTools(user.toollist).filter(
        (tool) => tool !== 'editor' && tool !== 'admin'
      );

      store.commit('SET_CURRENT_USER_ALLOWED_TOOLS', allowedTools);

      const isPendingOrRejected = user.isPendingApproval || user.isRejected;
      if (
        !isPendingOrRejected &&
        to.path === '/new-user-welcome-page' &&
        !isDebugWelcomePreview
      ) {
        return next('/view/dashboard');
      }

      // Ensure the user has permission for the route's tool
      const pathSegments = to.path.split('/');
      const isAccessingTool = pathSegments.length > 2;

      if (isAccessingTool) {
        const tool = pathSegments[2];
        const defaultTools = ['dashboard', 'profile', 'version', 'fleetdata'];

        const hasPermission =
          defaultTools.includes(tool) ||
          allowedTools.includes(tool) ||
          roles.some((allowed) => allowed === tool);

        if (!hasPermission) {
          console.warn('No Permissions');
          return next('/404');
        }
      }

      return next();
    }

    // Check if the route requires authentication
    const requiresAuth = to.matched.some((r) => r.meta.authRequired);
    // Check if we need to refresh token (e.g., polymer endpoints)
    const shouldRefreshToken = to.matched.some((r) => r.meta.refreshToken);

    if (!requiresAuth) {
      return next();
    }

    // Attempt to acquire a token
    let accessToken;
    try {
      accessToken = await acquireToken(shouldRefreshToken);
    } catch (error) {
      accessToken = null;
    }

    // Validate acquired token
    let isTokenValid = false;
    if (accessToken) {
      const decoded = jwt_decode(accessToken);
      // Check if expiration time is still in the future
      isTokenValid = decoded.exp * 1000 > Date.now();
    }

    // If token invalid, redirect to signin
    if (!isTokenValid) {
      if (isInteractiveAuthInProgress()) {
        return next(false);
      }
      localStorage.setItem('path', to.path);
      return next({ path: '/signin' });
    }

    const cachedUserId = store.state.currentUser && store.state.currentUser._id;
    // Module switches reuse this online session and do not wait.
    // Stale profiles refresh in the background. Going offline clears it.
    if (getLoadedOnlineUserId() && cachedUserId === getLoadedOnlineUserId()) {
      store.dispatch('ensureCurrentUserProfile').catch(() => {});
      return authorizeLoadedUser(to, from, next, isDebugWelcomePreview);
    }

    let isNewUser = false;
    await store.dispatch('ensureCurrentUserProfile').catch(() => {
      isNewUser = true;
    });

    if (isNewUser || !store.state.currentUser || !store.state.currentUser._id) {
      localStorage.setItem('path', to.path);
      return next({ path: '/signin' });
    }

    return authorizeLoadedUser(to, from, next, isDebugWelcomePreview);
  } catch (error) {
    if (error?.type === 'aborted' || error?.message?.includes('Redirected')) {
      return;
    }
    console.error('Router guard error:', error);
    return next();
  }
});

router.afterEach((to) => {
  if (!isWorkInstructionsRoute(to)) {
    store.commit('SET_PROGRESS_BAR', {
      key: WORK_INSTRUCTIONS_LOADING_KEY,
      value: null,
    });
  }

  if (!isInstructionPackagesRoute(to)) {
    store.commit('SET_PROGRESS_BAR', {
      key: INSTRUCTION_PACKAGES_LOADING_KEY,
      value: null,
    });
  }

  if (!isStandardTasksRoute(to)) {
    store.commit('SET_PROGRESS_BAR', {
      key: STANDARD_TASKS_LOADING_KEY,
      value: null,
    });
  }

  // Clear any residual query parameters after navigation (e.g., token in link redirects)
});

window.vueRouter = router;

export default router;
